import { useEffect, useRef } from 'react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import type { AgentId, CardPushItem, SquadId, TriageCardRow } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { realtimeRepository } from '@web/data/repositories';
import type {
  ConversationBoardResponse,
  TaskBoardResponse,
  TaskWithCard,
} from '@web/data/repositories';
import {
  applyConversationItems,
  applyMyTasks,
  applyTaskItems,
  applyTriageItems,
  dropConversationStrays,
  dropMyTaskStrays,
  dropTaskStrays,
} from '@web/ui/board/utils/apply_card_push';

interface GroupedItems {
  /** Board items (conversation/task) grouped by their squad. */
  bySquad: Map<SquadId, CardPushItem[]>;
  /** Triage items are org-flat (no squad). */
  triageItems: CardPushItem[];
  /** Where each upserted card now lives — drives stray eviction. */
  convTarget: Map<string, SquadId>;
  /** Where each upserted task now lives — drives stray eviction. */
  taskTarget: Map<string, SquadId>;
}

/** Splits an envelope into squad-grouped board items, triage items, and the
 *  card/task → squad maps used to evict strays from other boards. */
function groupItems(items: readonly CardPushItem[]): GroupedItems {
  const bySquad = new Map<SquadId, CardPushItem[]>();
  const triageItems: CardPushItem[] = [];
  const convTarget = new Map<string, SquadId>();
  const taskTarget = new Map<string, SquadId>();
  for (const item of items) {
    if (item.kind === 'triage_upsert' || item.kind === 'triage_remove') {
      triageItems.push(item);
      continue;
    }
    const arr = bySquad.get(item.squadId) ?? [];
    arr.push(item);
    bySquad.set(item.squadId, arr);
    if (item.kind === 'conversation_upsert') convTarget.set(item.card.id, item.squadId);
    else if (item.kind === 'task_upsert') taskTarget.set(item.task.id, item.squadId);
  }
  return { bySquad, triageItems, convTarget, taskTarget };
}

/** Patches the conversation / task / my-tasks caches of one squad with its
 *  batch of items. Skips a cache the operator hasn't opened (no entry yet). */
function patchSquadBoards(
  queryClient: QueryClient,
  orgId: string,
  squadId: SquadId,
  items: CardPushItem[],
  myAgentId: AgentId | null,
): void {
  const conversationItems = items.filter(
    (i) => i.kind === 'conversation_upsert' || i.kind === 'conversation_remove',
  );
  const taskItems = items.filter((i) => i.kind === 'task_upsert' || i.kind === 'task_remove');

  const convKey = queryKeys.boards.squadConversation(orgId, squadId);
  const conv = queryClient.getQueryData<ConversationBoardResponse>(convKey);
  if (conv && conversationItems.length > 0) {
    queryClient.setQueryData(convKey, applyConversationItems(conv, conversationItems));
  }

  const tasksKey = queryKeys.boards.squadTasks(orgId, squadId);
  const tasks = queryClient.getQueryData<TaskBoardResponse>(tasksKey);
  if (tasks && taskItems.length > 0) {
    queryClient.setQueryData(tasksKey, applyTaskItems(tasks, taskItems, myAgentId));
  }

  const myKey = queryKeys.boards.squadMyTasks(orgId, squadId);
  const my = queryClient.getQueryData<TaskWithCard[]>(myKey);
  if (my && taskItems.length > 0 && myAgentId != null) {
    queryClient.setQueryData(myKey, applyMyTasks(my, taskItems, myAgentId));
  }
}

/** Drops, from every open squad cache under `keyPrefix`, the ids that now live
 *  in a different squad (`target` maps id → its current squad; the squad is the
 *  last key segment). Only writes a cache that actually changed. */
function evictFromSquadCaches<T>(
  queryClient: QueryClient,
  keyPrefix: readonly unknown[],
  target: ReadonlyMap<string, SquadId>,
  drop: (data: T, strays: ReadonlySet<string>) => T,
): void {
  for (const [key, data] of queryClient.getQueriesData<T>({ queryKey: keyPrefix })) {
    if (!data) continue;
    const squadId = key[4] as SquadId;
    const strays = new Set<string>();
    for (const [id, sq] of target) if (sq !== squadId) strays.add(id);
    const next = drop(data, strays);
    if (next !== data) queryClient.setQueryData(key, next);
  }
}

/**
 * Enforces the "a card/task lives in exactly one squad" invariant on the client:
 * an upsert naming squad B evicts that card/task from every OTHER open squad
 * board (re-triage / transfer). The watcher has no pre-image of the old squad,
 * so it can't emit that remove server-side — the client does it here.
 */
function evictStrays(
  queryClient: QueryClient,
  orgId: string,
  convTarget: ReadonlyMap<string, SquadId>,
  taskTarget: ReadonlyMap<string, SquadId>,
): void {
  if (convTarget.size > 0) {
    evictFromSquadCaches<ConversationBoardResponse>(
      queryClient,
      ['boards', 'squad', 'conversation', orgId],
      convTarget,
      dropConversationStrays,
    );
  }
  if (taskTarget.size > 0) {
    evictFromSquadCaches<TaskBoardResponse>(
      queryClient,
      ['boards', 'squad', 'tasks', orgId],
      taskTarget,
      dropTaskStrays,
    );
    evictFromSquadCaches<TaskWithCard[]>(
      queryClient,
      ['boards', 'squad', 'my-tasks', orgId],
      taskTarget,
      dropMyTaskStrays,
    );
  }
}

/** Patches the org's triage inbox cache (org-flat) with its batch. */
function patchTriageInbox(
  queryClient: QueryClient,
  orgId: string,
  triageItems: CardPushItem[],
): void {
  if (triageItems.length === 0) return;
  const triageKey = queryKeys.triage.inbox(orgId);
  const inbox = queryClient.getQueryData<TriageCardRow[]>(triageKey);
  if (inbox) queryClient.setQueryData(triageKey, applyTriageItems(inbox, triageItems));
}

/**
 * Opens the org's board-push SSE stream for the whole session and patches the
 * live TanStack caches as batches arrive. Mounted once high in the tree
 * (`OrgWorkspaceLayout`). Only patches caches that already exist — a board the
 * operator hasn't opened is a no-op (it refetches fresh when mounted). The
 * `hello` frame carries the viewer's agent id, used to split "my tasks" from
 * the task board columns.
 */
export function useKanbanStream(orgId: string): void {
  const queryClient = useQueryClient();
  const myAgentId = useRef<AgentId | null>(null);
  // Syncing an external subscription (SSE) into React Query is the sanctioned
  // `useEffect` case; re-subscribes when the org changes, unsubscribes on unmount.
  useEffect(() => {
    // Clear stale identity until this org's `hello` arrives — otherwise early
    // task frames for the new org could be split with the previous org's agent.
    myAgentId.current = null;
    return realtimeRepository.subscribeKanban(orgId, {
      onHello: (agentId) => {
        myAgentId.current = agentId;
      },
      onEnvelope: (envelope) => {
        const { bySquad, triageItems, convTarget, taskTarget } = groupItems(envelope.items);
        for (const [squadId, items] of bySquad) {
          patchSquadBoards(queryClient, orgId, squadId, items, myAgentId.current);
        }
        // A card moved to its named squad must leave every other open squad board.
        evictStrays(queryClient, orgId, convTarget, taskTarget);
        patchTriageInbox(queryClient, orgId, triageItems);
      },
    });
  }, [orgId, queryClient]);
}
